import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigConstants } from '../config/config-constants';
import { DisplayDecorator } from '../decorators/display-decorator';
import { StatusModel } from '../models/low-level/status.model';
import { ValueHelperModel } from '../models/low-level/value-helper.model';
import { TextModel } from '../models/primitive/text.model';
import { FormParticipantTitles } from '../types/form-participant-titles';
import { InitializableInterface } from './initializable.interface';

/**
 * Public contract of the value-help service: the reference data loaded once from OData
 * (and re-loaded on sync) plus the getters that filter/sort/map it for dropdowns and
 * lookups. Extends {@link InitializableInterface} (`isInitialized` + `init()`).
 *
 * Implemented by both the legacy `ValueHelperService` and the NgRx-backed
 * `ValueHelperNgrxService`; consumers depend on this interface via
 * {@link ValueHelperInstanceToken} rather than on a concrete class.
 */
export interface ValueHelperInterface extends InitializableInterface {
    /**
     * Codes collected per value-help ID. Populated for the FOC additional labor/material
     * code lists so the confirmation/material consumption screens can test membership.
     */
    codeListForId: Record<string, string[]>;

    /** Load the value helpers from the backend (also re-run on sync completion). */
    init(): Observable<void>;

    getReasonsMissingProtocolsByCode(codeLevel: string): ValueHelperModel[];
    getNotificationTypeLabel(notificationType: ConfigConstants.NotificationTypes): string;
    getEquipmentBKInfo(): ValueHelperModel[];
    getCommercialInfoDefaultCode(): ValueHelperModel[];
    getNotificationEffectCode(): ValueHelperModel[];
    getEquipmentErrorCode(codeLevel: string): ValueHelperModel[];
    getEquipmentReason(): ValueHelperModel[];
    getEquipmentStatusAfterFix(): ValueHelperModel[];
    getEquipmentStatusInArrival(): ValueHelperModel[];
    getCentriCareStatusAfterFix(): ValueHelperModel[];
    getCentriCareStatInArrival(): ValueHelperModel[];
    getExpenseType(): ValueHelperModel[];
    getMaterialMK(): Array<DisplayDecorator<TextModel>>;
    getTimeRecordingBillableCodes(): ValueHelperModel[];
    getTimeRecordingTaskTypeInfo(): ValueHelperModel[];
    getRemoteWorkTimeRecordingTaskTypeInfo(status: StatusModel): ValueHelperModel[];
    getPhoneFixIndicator(): boolean;
    getMeansOfTransportation(): ValueHelperModel[];
    getTravelDestination(): ValueHelperModel[];
    getTimesheetGapTypeInfo(): ValueHelperModel[];
    getCreateMatOrderPriority(): ValueHelperModel[];
    getFollowUpUMRCode(): ValueHelperModel[];
    getBillable(): Array<DisplayDecorator<TextModel>>;
    getMaterialErrorTubeJ(): Array<DisplayDecorator<TextModel>>;
    getMaterialErrorTubeI(): Array<DisplayDecorator<TextModel>>;
    getMaterialBug(): Array<DisplayDecorator<TextModel>>;
    getReturnType(): Array<DisplayDecorator<TextModel>>;
    getMaterialReturnCause(): Array<DisplayDecorator<TextModel>>;
    getMaterialPhiDataCode(): Array<DisplayDecorator<TextModel>>;
    getQuoteType(): ValueHelperModel[];
    getComplaintQuestions(): ValueHelperModel[];
    getFollowDispatchTime(): ValueHelperModel[];
    getQualityControlQuestions(): ValueHelperModel[];
    getTimesheetActivityTypeInfo(): ValueHelperModel[];
    getPdaConfigValues(): ValueHelperModel[];
    getOsrLimitValueHelper(): ValueHelperModel | undefined;
    getAttendeeTitles(): ValueHelperModel[];
    getParticipantTitles(): FormParticipantTitles;
    getModalityValue(modality: string): ValueHelperModel | undefined;
    getFollowupReason(): ValueHelperModel[];
    getSRSVPNConnection(): ValueHelperModel[];
    getAddMaterialTypes(): ValueHelperModel[];
    getDangerousGoodWarning(): ValueHelperModel[];
    getFurtherLinks(): ValueHelperModel[];
    getTravelOfficeStart(): ValueHelperModel[];
    getCustomerSignatureAgreementTextClause(): string;
    getUploadToDocboxValueHelpers(): ValueHelperModel[];
    getMaterialConsumptionFieldsLabels(): ValueHelperModel[];
    getStatesOfUSA(): ValueHelperModel[];
    getFDAFormIntendedUses(): ValueHelperModel[];
    getXRaySystems(): ValueHelperModel[];
    getFDAFormCertifiedComponent(): ValueHelperModel[];
    getFDAFormOtherCertifiedComponent(): ValueHelperModel[];
    getFDAFormComponentInfo(): ValueHelperModel[];
}

/**
 * DI token for {@link ValueHelperInterface}. Lets the app choose which implementation to
 * provide (legacy `ValueHelperService` or the NgRx-based `ValueHelperNgrxService`) without
 * consumers depending on a concrete class.
 */
export const ValueHelperInstanceToken = new InjectionToken<ValueHelperInterface>('ValueHelperInterface');
